import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { User, Role, Permission, Department, BillingInfo, UserStatistics, RoleStatistics } from '../../services/adminApi';

// State interfaces
export interface AdminState {
  // User management
  users: {
    items: User[];
    selectedUser: User | null;
    loading: boolean;
    error: string | null;
    validation: Record<string, string>;
    pagination: {
      page: number;
      limit: number;
      total: number;
    };
    filters: {
      search: string;
      active?: boolean;
      roleId?: string;
      departmentId?: string;
    };
    statistics: UserStatistics | null;
    bulkSelection: string[];
  };

  // Role management
  roles: {
    items: Role[];
    selectedRole: Role | null;
    loading: boolean;
    error: string | null;
    validation: Record<string, string>;
    pagination: {
      page: number;
      limit: number;
      total: number;
    };
    filters: {
      search: string;
      active?: boolean;
    };
    statistics: RoleStatistics | null;
    bulkSelection: string[];
  };

  // Dropdown data
  dropdowns: {
    departments: Department[];
    permissions: Permission[];
    allRoles: Role[];
    loading: boolean;
    error: string | null;
  };

  // UI states
  ui: {
    userDialogOpen: boolean;
    roleDialogOpen: boolean;
    passwordResetDialogOpen: boolean;
    permissionDialogOpen: boolean;
    bulkActionsDialogOpen: boolean;
    currentView: 'users' | 'roles';
    showUserImage: boolean;
    userImageUrl: string | null;
  };

  // Billing/subscription
  subscription: {
    billing: BillingInfo | null;
    loading: boolean;
    error: string | null;
  };
}

const initialState: AdminState = {
  users: {
    items: [],
    selectedUser: null,
    loading: false,
    error: null,
    validation: {},
    pagination: {
      page: 1,
      limit: 10,
      total: 0,
    },
    filters: {
      search: '',
      active: undefined,
      roleId: undefined,
      departmentId: undefined,
    },
    statistics: null,
    bulkSelection: [],
  },
  roles: {
    items: [],
    selectedRole: null,
    loading: false,
    error: null,
    validation: {},
    pagination: {
      page: 1,
      limit: 10,
      total: 0,
    },
    filters: {
      search: '',
      active: undefined,
    },
    statistics: null,
    bulkSelection: [],
  },
  dropdowns: {
    departments: [],
    permissions: [],
    allRoles: [],
    loading: false,
    error: null,
  },
  ui: {
    userDialogOpen: false,
    roleDialogOpen: false,
    passwordResetDialogOpen: false,
    permissionDialogOpen: false,
    bulkActionsDialogOpen: false,
    currentView: 'users',
    showUserImage: false,
    userImageUrl: null,
  },
  subscription: {
    billing: null,
    loading: false,
    error: null,
  },
};

const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    // User actions
    fetchUsersStart: (state) => {
      state.users.loading = true;
      state.users.error = null;
    },
    fetchUsersSuccess: (state, action: PayloadAction<{ users: User[]; total: number; page: number }>) => {
      state.users.loading = false;
      state.users.items = action.payload.users;
      state.users.pagination.total = action.payload.total;
      state.users.pagination.page = action.payload.page;
    },
    fetchUsersFailure: (state, action: PayloadAction<string>) => {
      state.users.loading = false;
      state.users.error = action.payload;
    },

    setSelectedUser: (state, action: PayloadAction<User | null>) => {
      state.users.selectedUser = action.payload;
      state.users.validation = {};
    },

    setUserValidation: (state, action: PayloadAction<Record<string, string>>) => {
      state.users.validation = action.payload;
    },

    setUserFilters: (state, action: PayloadAction<Partial<AdminState['users']['filters']>>) => {
      state.users.filters = { ...state.users.filters, ...action.payload };
      state.users.pagination.page = 1; // Reset to first page when filters change
    },

    setUserPagination: (state, action: PayloadAction<{ page: number; limit: number }>) => {
      state.users.pagination.page = action.payload.page;
      state.users.pagination.limit = action.payload.limit;
    },

    setUserBulkSelection: (state, action: PayloadAction<string[]>) => {
      state.users.bulkSelection = action.payload;
    },

    setUserStatistics: (state, action: PayloadAction<UserStatistics>) => {
      state.users.statistics = action.payload;
    },

    // Role actions
    fetchRolesStart: (state) => {
      state.roles.loading = true;
      state.roles.error = null;
    },
    fetchRolesSuccess: (state, action: PayloadAction<{ roles: Role[]; total: number; page: number }>) => {
      state.roles.loading = false;
      state.roles.items = action.payload.roles;
      state.roles.pagination.total = action.payload.total;
      state.roles.pagination.page = action.payload.page;
    },
    fetchRolesFailure: (state, action: PayloadAction<string>) => {
      state.roles.loading = false;
      state.roles.error = action.payload;
    },

    setSelectedRole: (state, action: PayloadAction<Role | null>) => {
      state.roles.selectedRole = action.payload;
      state.roles.validation = {};
    },

    setRoleValidation: (state, action: PayloadAction<Record<string, string>>) => {
      state.roles.validation = action.payload;
    },

    setRoleFilters: (state, action: PayloadAction<Partial<AdminState['roles']['filters']>>) => {
      state.roles.filters = { ...state.roles.filters, ...action.payload };
      state.roles.pagination.page = 1; // Reset to first page when filters change
    },

    setRolePagination: (state, action: PayloadAction<{ page: number; limit: number }>) => {
      state.roles.pagination.page = action.payload.page;
      state.roles.pagination.limit = action.payload.limit;
    },

    setRoleBulkSelection: (state, action: PayloadAction<string[]>) => {
      state.roles.bulkSelection = action.payload;
    },

    setRoleStatistics: (state, action: PayloadAction<RoleStatistics>) => {
      state.roles.statistics = action.payload;
    },

    // Dropdown actions
    fetchDropdownsStart: (state) => {
      state.dropdowns.loading = true;
      state.dropdowns.error = null;
    },
    fetchDropdownsSuccess: (state, action: PayloadAction<{
      departments: Department[];
      permissions: Permission[];
      allRoles: Role[];
    }>) => {
      state.dropdowns.loading = false;
      state.dropdowns.departments = action.payload.departments;
      state.dropdowns.permissions = action.payload.permissions;
      state.dropdowns.allRoles = action.payload.allRoles;
    },
    fetchDropdownsFailure: (state, action: PayloadAction<string>) => {
      state.dropdowns.loading = false;
      state.dropdowns.error = action.payload;
    },

    // UI actions
    setUserDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.ui.userDialogOpen = action.payload;
      if (!action.payload) {
        state.users.selectedUser = null;
        state.users.validation = {};
      }
    },

    setRoleDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.ui.roleDialogOpen = action.payload;
      if (!action.payload) {
        state.roles.selectedRole = null;
        state.roles.validation = {};
      }
    },

    setPasswordResetDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.ui.passwordResetDialogOpen = action.payload;
    },

    setPermissionDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.ui.permissionDialogOpen = action.payload;
    },

    setBulkActionsDialogOpen: (state, action: PayloadAction<boolean>) => {
      state.ui.bulkActionsDialogOpen = action.payload;
    },

    setCurrentView: (state, action: PayloadAction<'users' | 'roles'>) => {
      state.ui.currentView = action.payload;
    },

    setShowUserImage: (state, action: PayloadAction<boolean>) => {
      state.ui.showUserImage = action.payload;
    },

    setUserImageUrl: (state, action: PayloadAction<string | null>) => {
      state.ui.userImageUrl = action.payload;
    },

    // Subscription actions
    fetchSubscriptionStart: (state) => {
      state.subscription.loading = true;
      state.subscription.error = null;
    },
    fetchSubscriptionSuccess: (state, action: PayloadAction<BillingInfo>) => {
      state.subscription.loading = false;
      state.subscription.billing = action.payload;
    },
    fetchSubscriptionFailure: (state, action: PayloadAction<string>) => {
      state.subscription.loading = false;
      state.subscription.error = action.payload;
    },

    // Clear errors
    clearUserError: (state) => {
      state.users.error = null;
    },
    clearRoleError: (state) => {
      state.roles.error = null;
    },
    clearDropdownError: (state) => {
      state.dropdowns.error = null;
    },
    clearSubscriptionError: (state) => {
      state.subscription.error = null;
    },

    // Reset state
    resetUserState: (state) => {
      state.users = initialState.users;
    },
    resetRoleState: (state) => {
      state.roles = initialState.roles;
    },
    resetAdminState: () => initialState,
  },
});

export const {
  // User actions
  fetchUsersStart,
  fetchUsersSuccess,
  fetchUsersFailure,
  setSelectedUser,
  setUserValidation,
  setUserFilters,
  setUserPagination,
  setUserBulkSelection,
  setUserStatistics,

  // Role actions
  fetchRolesStart,
  fetchRolesSuccess,
  fetchRolesFailure,
  setSelectedRole,
  setRoleValidation,
  setRoleFilters,
  setRolePagination,
  setRoleBulkSelection,
  setRoleStatistics,

  // Dropdown actions
  fetchDropdownsStart,
  fetchDropdownsSuccess,
  fetchDropdownsFailure,

  // UI actions
  setUserDialogOpen,
  setRoleDialogOpen,
  setPasswordResetDialogOpen,
  setPermissionDialogOpen,
  setBulkActionsDialogOpen,
  setCurrentView,
  setShowUserImage,
  setUserImageUrl,

  // Subscription actions
  fetchSubscriptionStart,
  fetchSubscriptionSuccess,
  fetchSubscriptionFailure,

  // Clear errors
  clearUserError,
  clearRoleError,
  clearDropdownError,
  clearSubscriptionError,

  // Reset state
  resetUserState,
  resetRoleState,
  resetAdminState,
} = adminSlice.actions;

export const adminReducer = adminSlice.reducer; 